/**
 * Tally configuration — where every posting lands and when the agent runs. Read-only.
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero (through `components/detail-parts.tsx`'s `PageHero`);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks in every card;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the timing tiles;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-overview.tsx
 *   the bordered callouts (posting mode, changing any of this).
 *
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ReactNode } from "react";
import { Link, useLoaderData, useOutletContext } from "react-router-dom";
import {
  PiArrowRightBold as ArrowRight,
  PiArrowsClockwiseDuotone as BackoffIcon,
  PiBookOpenTextDuotone as LedgerIcon,
  PiBuildingsDuotone as CompanyIcon,
  PiCalendarCheckDuotone as ScheduleIcon,
  PiClockDuotone as ClockIcon,
  PiCoinsDuotone as ReceiptsIcon,
  PiGearSixDuotone as ConfigIcon,
  PiHeartbeatDuotone as HeartbeatIcon,
  PiInfoDuotone as InfoIcon,
  PiLockKeyDuotone as LeaseIcon,
  PiShieldCheckDuotone as ConfirmedIcon,
  PiShieldWarningDuotone as AuthIcon,
  PiStackDuotone as SubGroupIcon,
  PiTimerDuotone as TimerIcon,
  PiTrayDuotone as QueueIcon,
  PiUsersDuotone as CustomersIcon,
  PiVaultDuotone as RetentionIcon,
  PiWarningDuotone as WarningIcon,
} from "react-icons/pi";
import { Badge as TagBadge, Button, Text } from "rizzui";
import { Badge, CaveatBanner, ErrorSurface, WidgetCard, cn } from "@steelyard/theme";
import { CardTitle, InfoBlock, PageHero } from "../../../components/detail-parts";
import { can } from "../../../lib/permissions";
import { labelOf, tierNames, type Labels } from "../../../lib/lookups";
import { getTallyConfig, camelCaseLabel, clockTimeLabel, cronLabel, durationLabel, type TallyConfig } from "../../../lib/services/tallySync";
import type { AccountSummary, PermissionState } from "../../../types";

/**
 * Ported from apps/admin/app/(app)/integrations/tally/config/page.tsx. Read-only by contract, not
 * by shortcut — the backend's `PUT` was never built; every value here is platform configuration,
 * and a write route here would be a second, disagreeing copy of it. **Do not add a write form.**
 */
export async function configLoader() {
  // Per-tier sub-groups are keyed by tier id: shown by the tier's name, the id only as a fallback.
  const [config, tiers] = await Promise.all([getTallyConfig(), tierNames()]);
  return { ...config, tierNames: tiers };
}

/** The week, to draw `businessDays` against. Display only — which days count is the API's list. */
const WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function TallyConfigPage() {
  // Both hooks called unconditionally, before the permission early-return — React's Rules of
  // Hooks forbid a conditional hook call, even though the loader's fetch goes unused on refusal.
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const data = useLoaderData() as TallyConfig & { tierNames: Labels };

  if (!can(permissionState, "integrations.read")) {
    return (
      <ErrorSurface
        error={{
          title: "Not permitted",
          status: 403,
          detail: "Tally configuration is a Super Admin surface. It names the ledgers and voucher types every posting goes to, so reading it is gated with writing it.",
        }}
        action="view the Tally configuration"
      />
    );
  }

  const confirmed = data.i05Confirmed;
  const tierSubGroups = Object.entries(data.perTierSubGroups ?? {});
  const voucherTypes = Object.entries(data.voucherTypes ?? {});
  const unconfirmed = confirmed ? null : <Badge kind="stale">Unconfirmed</Badge>;

  const body = (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3 rounded-xl border border-s-4 border-muted border-s-blue bg-blue-lighter/30 px-5 py-4 dark:bg-blue-lighter/10">
        <InfoIcon aria-hidden className="mt-0.5 size-5 shrink-0 text-blue-dark" />
        <div>
          <Text className="font-semibold text-gray-900">Posting mode: accounting vouchers only</Text>
          <Text className="mt-0.5 text-sm text-gray-600">
            Invoices and credit notes post as accounting vouchers with <strong>ledger allocations only — no inventory
            allocations</strong>.
          </Text>
        </div>
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-2">
        {/* ── Ledgers and voucher types ─────────────────────────────────────────────── */}
        <WidgetCard
          rounded="xl"
          title={
            <CardTitle icon={<LedgerIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
              Ledgers and voucher types
            </CardTitle>
          }
          headerClassName="items-center"
          description={<Text className="mt-2 text-sm text-gray-500">Where postings land.</Text>}
        >
          <div className="mt-5 flex flex-col gap-5">
            <InfoBlock icon={<CompanyIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Company">
              <Text className="font-medium text-gray-900">{data.companyName || "not set"}</Text>
            </InfoBlock>

            <InfoBlock icon={<LedgerIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Ledger group">
              <span className="flex flex-wrap items-center gap-2">
                <Text className="font-medium text-gray-900">{data.ledgerGroup || "not set"}</Text>
                {unconfirmed}
              </span>
            </InfoBlock>

            <InfoBlock icon={<SubGroupIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Per-tier sub-groups">
              {tierSubGroups.length === 0 ? (
                <Text className="text-sm text-gray-500">
                  None configured. The per-tier sub-group is optional — with none set, every customer ledger sits directly under
                  the ledger group above.
                </Text>
              ) : (
                <ul className="mt-1 flex flex-col gap-2">
                  {tierSubGroups.map(([tierId, group]) => (
                    <li key={tierId} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="break-all text-xs text-gray-500">{labelOf(data.tierNames, tierId)}</span>
                      <ArrowRight aria-hidden className="size-3 text-gray-400" />
                      <span className="font-medium text-gray-900">{group}</span>
                      {unconfirmed}
                    </li>
                  ))}
                </ul>
              )}
            </InfoBlock>

            <div className="border-t border-dashed border-muted" />

            <div>
              <Text className="text-xs font-semibold uppercase tracking-wider text-gray-500">Voucher types</Text>
              {voucherTypes.length === 0 ? (
                <Text className="mt-1 text-sm text-gray-500">None configured. Nothing can post until the client&rsquo;s Tally operator names them.</Text>
              ) : (
                <ul className="mt-3 flex flex-col gap-2">
                  {voucherTypes.map(([document, voucher]) => (
                    <li key={document} className="flex flex-wrap items-center gap-3 rounded-lg border border-muted px-4 py-3">
                      <TagBadge variant="flat" color="secondary" rounded="pill">
                        {camelCaseLabel(document)}
                      </TagBadge>
                      <ArrowRight aria-hidden className="size-3.5 text-gray-400" />
                      <span className="font-semibold text-gray-900">{voucher}</span>
                      <span className="ms-auto">{unconfirmed}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </WidgetCard>

        <div className="flex flex-col gap-6">
          {/* ── Pull schedules + business hours ──────────────────────────────────────── */}
          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<ScheduleIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
                Pull schedules and business hours
              </CardTitle>
            }
            headerClassName="items-center"
            description={<Text className="mt-2 text-sm text-gray-500">When the agent pulls from Tally, and when silence counts as a fault.</Text>}
          >
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <InfoBlock icon={<CustomersIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Customer pull">
                <Text className="font-medium text-gray-900">{cronLabel(data.customerPullSchedule)}</Text>
                <Text className="font-mono text-xs text-gray-500">{data.customerPullSchedule}</Text>
              </InfoBlock>
              <InfoBlock icon={<ReceiptsIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Receipt pull">
                <Text className="font-medium text-gray-900">{cronLabel(data.receiptPullSchedule)}</Text>
                <Text className="font-mono text-xs text-gray-500">{data.receiptPullSchedule}</Text>
              </InfoBlock>
            </div>

            <div className="mt-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-100/40">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Text className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <ClockIcon aria-hidden className="size-4 text-blue-dark" />
                  {clockTimeLabel(data.businessHoursStart)} – {clockTimeLabel(data.businessHoursEnd)}
                </Text>
                <Text className="text-xs text-gray-500">Business hours</Text>
              </div>
              {data.businessDays.length === 0 ? (
                <Text className="mt-3 text-sm text-gray-500">Days: none set</Text>
              ) : (
                <ul aria-label="Business days" className="mt-3 flex flex-wrap gap-1.5">
                  {WEEK.map((day) => {
                    const on = data.businessDays.includes(day);
                    return (
                      <li key={day}>
                        <TagBadge variant={on ? "solid" : "outline"} color={on ? "info" : undefined} rounded="md" className={cn(!on && "border-muted text-gray-500 line-through")}>
                          <span aria-hidden>{day.slice(0, 3)}</span>
                          <span className="sr-only">{on ? `${day}: a business day` : `${day}: not a business day`}</span>
                        </TagBadge>
                      </li>
                    );
                  })}
                </ul>
              )}
              {data.businessDays.some((day) => !WEEK.includes(day)) ? (
                <Text className="mt-2 text-xs text-gray-500">Days as the service sent them: {data.businessDays.join(", ")}</Text>
              ) : null}
            </div>
          </WidgetCard>

          {/* ── Heartbeat and retry timing ───────────────────────────────────────────── */}
          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<HeartbeatIcon aria-hidden className="size-5" />} tile="bg-red-lighter text-red-dark">
                Heartbeat and retry
              </CardTitle>
            }
            headerClassName="items-center"
          >
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <TimingTile icon={<TimerIcon aria-hidden className="size-5" />} tile="bg-red" label="Heartbeat alert period" value={durationLabel(data.heartbeatAlertPeriod)} />
              <TimingTile icon={<HeartbeatIcon aria-hidden className="size-5" />} tile="bg-green" label="Heartbeat poll interval" value={durationLabel(data.heartbeatPollInterval)} />
              <TimingTile icon={<LeaseIcon aria-hidden className="size-5" />} tile="bg-secondary" label="Work lease duration" value={durationLabel(data.leaseDuration)} />
              <TimingTile icon={<BackoffIcon aria-hidden className="size-5" />} tile="bg-blue" label="Retry backoff, base" value={durationLabel(data.retryBackoffBase)} />
              <TimingTile icon={<BackoffIcon aria-hidden className="size-5" />} tile="bg-blue" label="Retry backoff, cap" value={durationLabel(data.retryBackoffCap)} />
              <TimingTile icon={<AuthIcon aria-hidden className="size-5" />} tile="bg-red" label="Authentication failure alert threshold" value={String(data.authFailureAlertThreshold)} />
            </div>
          </WidgetCard>
        </div>
      </div>

      <WidgetCard
        rounded="xl"
        title={
          <CardTitle icon={<RetentionIcon aria-hidden className="size-5" />} tile="bg-gray-100 text-gray-600">
            Changing any of this
          </CardTitle>
        }
        headerClassName="items-center"
        description={<Text className="mt-2 text-sm text-gray-500">There is no edit form here.</Text>}
      >
        <div className="mt-5 grid gap-5 text-sm md:grid-cols-3">
          <p className="text-gray-600">
            Every value above is platform configuration. It is changed in the deployment&rsquo;s settings, and takes effect on
            the <strong>next posting</strong>, with no release and no restart.
          </p>
          <p className="text-gray-600">
            <span className="font-medium text-gray-900">What a change affects: </span>
            postings made after it. Records <strong>already queued</strong> carry the payload they were enqueued with —
            changing a voucher type does not rewrite them, and there is no route to edit a queued record&rsquo;s payload.
          </p>
          <p className="text-gray-600">
            Configuration changes are audit-logged by the service, and audit records are retained for {data.auditRetentionYears}{" "}
            years.
          </p>
        </div>
      </WidgetCard>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Integrations" }, { name: "Tally Agent", href: "/integrations/tally" }, { name: "Configuration" }]}
        icon={<ConfigIcon aria-hidden className="size-9" />}
        iconClassName="bg-blue"
        title="Tally Configuration"
        description="The ledger group, per-tier sub-groups, voucher types and schedules every posting uses."
        meta={
          <>
            {confirmed ? (
              <TagBadge variant="flat" color="success" rounded="pill" className="gap-1.5">
                <ConfirmedIcon aria-hidden className="size-4" />
                Confirmed with the Tally operator
              </TagBadge>
            ) : (
              <TagBadge variant="flat" color="warning" rounded="pill" className="gap-1.5">
                <WarningIcon aria-hidden className="size-4" />
                Unconfirmed defaults
              </TagBadge>
            )}
            <TagBadge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <CompanyIcon aria-hidden className="size-4" />
              {data.companyName || "Company not set"}
            </TagBadge>
            <TagBadge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <RetentionIcon aria-hidden className="size-4" />
              Audit kept {data.auditRetentionYears} years
            </TagBadge>
          </>
        }
        actions={
          <>
            <Link to="/integrations/tally/queue">
              <Button as="span" variant="outline">
                <QueueIcon aria-hidden className="me-1.5 size-4 text-secondary" />
                Sync queue
              </Button>
            </Link>
            <Link to="/integrations/tally">
              <Button as="span" variant="outline">
                <HeartbeatIcon aria-hidden className="me-1.5 size-4 text-green" />
                Agent health
              </Button>
            </Link>
          </>
        }
      />

      {confirmed ? (
        body
      ) : (
        <CaveatBanner
          caveat="Nobody has confirmed these names with the client's Tally operator."
          action={
            <span>
              Every value marked <span className="font-semibold">unconfirmed</span> below is a default, not a confirmed
              agreement.
            </span>
          }
        >
          {body}
        </CaveatBanner>
      )}
    </div>
  );
}

/** A duration or threshold with its solid colour tile — stats.tsx's tile, at card-row size. */
function TimingTile({ icon, tile, label, value }: { icon: ReactNode; tile: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-muted p-3 transition-shadow hover:shadow-sm">
      <span className={cn("flex size-10 shrink-0 items-center justify-center rounded-lg text-white", tile)}>{icon}</span>
      <div className="min-w-0">
        <Text className="text-xs text-gray-500">{label}</Text>
        <Text className="font-lexend font-semibold text-gray-900">{value}</Text>
      </div>
    </div>
  );
}
